/**
 * Text-to-Speech Service using Web Speech API
 * Completely free, no external services needed
 * Optimized for clarity and reduced noise
 */

export interface TTSOptions {
  text: string;
  lang?: string;
  rate?: number; // 0.1 to 10, default 1
  pitch?: number; // 0 to 2, default 1
  volume?: number; // 0 to 1, default 1
  voice?: SpeechSynthesisVoice;
  accent?: 'american' | 'british'; // Add accent type
}

export type AccentType = 'american' | 'british';

export class TTSService {
  private synthesis: SpeechSynthesis;
  private voices: SpeechSynthesisVoice[] = [];
  private currentVoice: SpeechSynthesisVoice | null = null;
  private isInitialized: boolean = false;
  private currentAccent: AccentType = 'american'; // Default American English
  private americanVoice: SpeechSynthesisVoice | null = null;
  private britishVoice: SpeechSynthesisVoice | null = null;

  constructor() {
    this.synthesis = window.speechSynthesis;
    this.loadVoices();
  }

  // Load available voices with better initialization
  private loadVoices(): void {
    this.voices = this.synthesis.getVoices();

    // Voices might load asynchronously in some browsers
    if (this.voices.length === 0) {
      // Chrome/Firefox: voices load asynchronously
      this.synthesis.onvoiceschanged = () => {
        this.voices = this.synthesis.getVoices();
        this.setDefaultVoice();
        this.isInitialized = true;
      };
    } else {
      // Safari/Edge: voices available immediately
      this.setDefaultVoice();
      this.isInitialized = true;
    }

    // Fallback timeout for initialization
    setTimeout(() => {
      if (!this.isInitialized) {
        this.voices = this.synthesis.getVoices();
        this.setDefaultVoice();
        this.isInitialized = true;
      }
    }, 1000);
  }

  // Set default voice - prioritize high-quality, clear voices
  private setDefaultVoice(): void {
    // American English voices (priority list)
    const americanVoices = [
      // macOS voices (very natural and clear)
      'Samantha', // macOS - excellent quality, female, American
      'Alex', // macOS - male, very natural, American

      // Windows voices
      'Microsoft Zira Online (Natural)', // Windows 11 - high quality American
      'Microsoft Zira', // Windows 10 - good quality American
      'Microsoft Mark', // Windows - male American

      // Chrome/Google voices
      'Google US English Female',
      'Google US English Male',

      // High-quality system voices
      'en-US-Standard-A',
      'en-US-Standard-B',
      'en-US',
    ];

    // British English voices (priority list)
    const britishVoices = [
      // macOS voices
      'Kate', // macOS - British female
      'Daniel', // macOS - British male (UK)

      // Windows voices
      'Microsoft Susan Online (Natural)', // Windows 11 British
      'Microsoft Susan', // Windows 10 British
      'Microsoft George', // Windows British male

      // Chrome/Google voices
      'Google UK English Female',
      'Google UK English Male',

      // Fallback
      'en-GB',
    ];

    // Select American voice
    for (const preferred of americanVoices) {
      const voice = this.voices.find(v =>
        v.name.includes(preferred) || v.lang.includes(preferred)
      );
      if (voice) {
        this.americanVoice = voice;
        console.log(`✅ American TTS Voice selected: ${voice.name} (${voice.lang})`);
        break;
      }
    }

    // Select British voice
    for (const preferred of britishVoices) {
      const voice = this.voices.find(v =>
        v.name.includes(preferred) || v.lang.includes(preferred)
      );
      if (voice) {
        this.britishVoice = voice;
        console.log(`✅ British TTS Voice selected: ${voice.name} (${voice.lang})`);
        break;
      }
    }

    // Set current voice based on accent preference
    this.currentVoice = this.currentAccent === 'british'
      ? this.britishVoice || this.americanVoice
      : this.americanVoice || this.britishVoice;

    // Fallback: first English voice
    if (!this.currentVoice) {
      this.currentVoice = this.voices.find(v => v.lang.startsWith('en')) || null;
      if (this.currentVoice) {
        console.log(`⚠️ TTS Voice fallback: ${this.currentVoice.name} (${this.currentVoice.lang})`);
      }
    }

    // Last fallback: first available voice
    if (!this.currentVoice && this.voices.length > 0) {
      this.currentVoice = this.voices[0];
      console.log(`⚠️ TTS Voice last fallback: ${this.currentVoice.name}`);
    }
  }

  // Switch accent type
  setAccent(accent: AccentType): void {
    this.currentAccent = accent;
    this.currentVoice = accent === 'british'
      ? this.britishVoice
      : this.americanVoice;

    console.log(`✅ Accent changed to: ${accent === 'british' ? 'British (英式)' : 'American (美式)'}`);

    if (!this.currentVoice) {
      console.warn(`⚠️ No ${accent} voice available, using fallback`);
    }
  }

  // Get current accent
  getAccent(): AccentType {
    return this.currentAccent;
  }

  // Get accent display name
  getAccentDisplayName(): string {
    return this.currentAccent === 'british' ? '英式英语' : '美式英语';
  }

  // Check if both accents are available
  hasBothAccents(): boolean {
    return !!this.americanVoice && !!this.britishVoice;
  }

  // Get all available voices
  getVoices(): SpeechSynthesisVoice[] {
    return this.voices.filter(v => v.lang.startsWith('en'));
  }

  // Get current voice info
  getCurrentVoice(): SpeechSynthesisVoice | null {
    return this.currentVoice;
  }

  // Set specific voice
  setVoice(voice: SpeechSynthesisVoice): void {
    this.currentVoice = voice;
    console.log(`✅ TTS Voice changed to: ${voice.name}`);
  }

  // Speak text with optimized settings for clarity
  speak(options: TTSOptions): Promise<void> {
    return new Promise((resolve, reject) => {
      // Cancel any ongoing speech to prevent overlap
      this.synthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(options.text);

      // Set voice (prefer high-quality voices)
      if (options.voice) {
        utterance.voice = options.voice;
      } else if (this.currentVoice) {
        utterance.voice = this.currentVoice;
      }

      // Set language based on accent
      const langCode = this.currentAccent === 'british' ? 'en-GB' : 'en-US';
      utterance.lang = options.lang || langCode;

      // Debug: log voice and accent info
      console.log(`🔊 TTS Speaking: "${options.text.substring(0, 20)}..."`);
      console.log(`   Accent: ${this.currentAccent}, Lang: ${utterance.lang}`);
      console.log(`   Voice: ${utterance.voice?.name || 'default'} (${utterance.voice?.lang || 'N/A'})`);
      console.log(`   American voice: ${this.americanVoice?.name || 'none'}`);
      console.log(`   British voice: ${this.britishVoice?.name || 'none'}`);
      if (!this.americanVoice || !this.britishVoice) {
        console.warn(`⚠️ Missing voice! american: ${!!this.americanVoice}, british: ${!!this.britishVoice}`);
      }

      // Optimized rate for clarity (slower is clearer)
      // Kids: 0.75-0.85, Adults: 0.85-1.0
      const defaultRate = 0.8; // Optimal for learning
      utterance.rate = options.rate !== undefined ? options.rate : defaultRate;

      // Optimized pitch for naturalness
      // Female: 1.0-1.1, Male: 0.9-1.0
      utterance.pitch = options.pitch || 1.0;

      // Volume: clear but not too loud (prevents distortion)
      utterance.volume = options.volume || 0.9;

      // Add text preprocessing to improve pronunciation
      const processedText = this.preprocessText(options.text);
      utterance.text = processedText;

      // Event handlers with error handling
      utterance.onstart = () => {
        console.log(`🔊 TTS started: "${options.text.substring(0, 30)}..."`);
      };

      utterance.onend = () => {
        console.log(`✅ TTS completed`);
        resolve();
      };

      utterance.onerror = (event) => {
        console.error(`❌ TTS error:`, event.error);
        // Don't reject for minor errors, just resolve
        if (event.error === 'interrupted' || event.error === 'canceled') {
          resolve();
        } else {
          reject(new Error(`TTS error: ${event.error}`));
        }
      };

      // Chrome bug workaround: resume synthesis if it gets stuck
      // https://bugs.chromium.org/p/chromium/issues/detail?id=335506
      const chromeFix = () => {
        if (this.synthesis.paused) {
          this.synthesis.resume();
        }
      };

      // Apply Chrome fix periodically during speech
      const intervalId = setInterval(chromeFix, 100);

      // Clear interval when speech ends
      utterance.onend = () => {
        clearInterval(intervalId);
        console.log(`✅ TTS completed`);
        resolve();
      };

      utterance.onerror = (event) => {
        clearInterval(intervalId);
        console.error(`❌ TTS error:`, event.error);
        if (event.error === 'interrupted' || event.error === 'canceled') {
          resolve();
        } else {
          reject(new Error(`TTS error: ${event.error}`));
        }
      };

      // Speak
      this.synthesis.speak(utterance);
    });
  }

  // Preprocess text to improve pronunciation
  private preprocessText(text: string): string {
    // Add pauses after punctuation for better rhythm
    let processed = text
      .replace(/\./g, '. ') // Add space after periods
      .replace(/!/g, '! ') // Add space after exclamation
      .replace(/\?/g, '? ') // Add space after question marks
      .replace(/,/g, ', '); // Add space after commas

    // Handle abbreviations
    processed = processed
      .replace(/Mr\./g, ' Mister ')
      .replace(/Mrs\./g, ' Misses ')
      .replace(/Dr\./g, ' Doctor ');

    // Clean up multiple spaces
    processed = processed.replace(/\s+/g, ' ').trim();

    return processed;
  }

  // Stop speaking
  stop(): void {
    this.synthesis.cancel();
    console.log('⏹️ TTS stopped');
  }

  // Check if currently speaking
  isSpeaking(): boolean {
    return this.synthesis.speaking;
  }

  // Check if paused
  isPaused(): boolean {
    return this.synthesis.paused;
  }

  // Pause speaking
  pause(): void {
    this.synthesis.pause();
    console.log('⏸️ TTS paused');
  }

  // Resume speaking
  resume(): void {
    this.synthesis.resume();
    console.log('▶️ TTS resumed');
  }

  // Speak word slowly (for learning pronunciation)
  speakSlowly(word: string): Promise<void> {
    return this.speak({
      text: word,
      rate: 0.65, // Very slow for pronunciation practice
      pitch: 1.0,
    });
  }

  // Speak with specific voice name
  speakWithVoice(text: string, voiceName: string): Promise<void> {
    const voice = this.voices.find(v => v.name.includes(voiceName));
    if (voice) {
      return this.speak({
        text,
        voice,
        rate: 0.8,
      });
    } else {
      // Fallback to current accent
      console.warn(`Voice "${voiceName}" not found, using default`);
      return this.speak({ text, rate: 0.8 });
    }
  }

  // Speak with specific accent
  speakWithAccent(text: string, accent: 'american' | 'british'): Promise<void> {
    const voice = accent === 'british' ? this.britishVoice : this.americanVoice;
    if (voice) {
      return this.speak({
        text,
        voice,
        rate: 0.8,
      });
    } else {
      // Temporarily set accent
      const originalAccent = this.currentAccent;
      this.setAccent(accent);
      return this.speak({ text, rate: 0.8 }).finally(() => {
        this.setAccent(originalAccent);
      });
    }
  }

  // Speak sentence at normal learning pace
  speakNormal(sentence: string): Promise<void> {
    return this.speak({
      text: sentence,
      rate: 0.8, // Clear and natural
      pitch: 1.0,
    });
  }

  // Speak with excitement (for rewards/achievements)
  speakExcited(text: string): Promise<void> {
    return this.speak({
      text,
      rate: 0.9, // Slightly faster
      pitch: 1.15, // Higher pitch = more excitement
      volume: 1.0,
    });
  }

  // Speak clearly for pronunciation teaching
  speakClear(text: string): Promise<void> {
    return this.speak({
      text,
      rate: 0.75, // Clear and slow
      pitch: 1.0,
      volume: 0.85,
    });
  }

  // Get voice quality info
  getVoiceQuality(): string {
    if (!this.currentVoice) return 'No voice selected';

    const voiceName = this.currentVoice.name;

    if (voiceName.includes('Samantha') || voiceName.includes('Alex')) {
      return 'Excellent (macOS native)';
    }
    if (voiceName.includes('Microsoft Zira') || voiceName.includes('Microsoft Mark')) {
      return 'Good (Windows native)';
    }
    if (voiceName.includes('Google')) {
      return 'Good (Google TTS)';
    }

    return 'Standard';
  }
}

// Create singleton instance
export const ttsService = new TTSService();